import { FC, memo, useCallback, useMemo, useState } from 'react'

import { InvertibleMultiSelectFooter } from '@capturi/ui-select'
import { Box, Divider, Popover, PopoverContent, Text } from '@chakra-ui/react'
import { Plural, Trans, t } from '@lingui/macro'
import { FilterSelect } from '../../../components/FilterSelect/components/FilterSelect'
import { MultiValueFilterItem } from '../useTextFilter'
import { useFilterValues } from './hooks/useFilterValues'
import { TextFilterPopoverComponentProps } from './types'

const InboxItem: FC<{
  inbox: string
  isSelected: boolean
  onSelect: (inbox: string) => void
}> = memo(({ inbox, isSelected, onSelect }) => {
  return (
    <FilterSelect.OptionItem
      key={inbox}
      isSelected={isSelected}
      onClick={() => onSelect(inbox)}
    >
      <Box>{inbox}</Box>
    </FilterSelect.OptionItem>
  )
})

export const InboxFilter: FC<
  TextFilterPopoverComponentProps<MultiValueFilterItem>
> = (props) => {
  const { data, isLoading } = useFilterValues({ name: 'Inbox' })
  const [searchQuery, setSearchQuery] = useState('')
  const filteredInboxes = useMemo(() => {
    if (!searchQuery) return data?.values
    return data?.values?.filter((inbox) =>
      inbox.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [searchQuery, data?.values])

  const selectedInboxSet = useMemo(
    () => new Set(props.item.values),
    [props.item.values],
  )

  const handleSelect = useCallback(
    (newInbox: string) => {
      const index = props.item.values
        ? props.item.values.findIndex((inbox) => inbox === newInbox)
        : -1
      if (index !== -1) {
        props.onChangeValue({
          ...props.item,
          values: props.item.values?.toSpliced(index, 1) ?? [],
        })
      } else {
        props.onChangeValue({
          ...props.item,
          values: [...(props.item.values ?? []), newInbox],
        })
      }
    },
    [props.item, props.onChangeValue],
  )
  const handleSelectAll = useCallback(() => {
    const newSelections = filteredInboxes
    props.onChangeValue({
      ...props.item,
      values: [...new Set([...(data?.values ?? []), ...(newSelections ?? [])])],
    })
    setSearchQuery('')
  }, [data?.values, props.item, filteredInboxes, props.onChangeValue])

  return (
    <Popover
      placement="bottom-start"
      isOpen={true}
      onClose={props.onClose}
      isLazy
    >
      <PopoverContent w={400}>
        <Box maxH="400px" display="flex" flexDirection="column">
          <Box px={2}>
            <FilterSelect.Search
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus={true}
            />
          </Box>
          <Divider />
          <Box flex={1} maxH="350px" overflowY="auto">
            {isLoading ? (
              <Text color="greys.400" textAlign="center" py={2}>
                <Trans>Loading...</Trans>
              </Text>
            ) : (
              <FilterSelect.FilterSelectOptions
                selected={props.item.values?.length ?? 0}
                total={data?.values?.length ?? 0}
                selectedItems={props.item.values?.map((inbox) => (
                  <InboxItem
                    key={inbox}
                    inbox={inbox}
                    isSelected={selectedInboxSet.has(inbox)}
                    onSelect={handleSelect}
                  />
                ))}
                unselectedItems={filteredInboxes?.map((inbox) => (
                  <InboxItem
                    key={inbox}
                    inbox={inbox}
                    isSelected={selectedInboxSet.has(inbox)}
                    onSelect={handleSelect}
                  />
                ))}
              />
            )}
          </Box>
          <Box flexShrink={0}>
            <InvertibleMultiSelectFooter
              entityName={t`Inbox(es)`}
              isNot={props.item.inverted}
              onChangeIsNot={(isNot) => {
                props.onChangeValue({
                  ...props.item,
                  inverted: isNot,
                })
              }}
              onReset={() =>
                props.onChangeValue({
                  ...props.item,
                  values: [],
                })
              }
              onSubmit={props.onClose}
              onSelectAllOptions={handleSelectAll}
              selectAllText={t`Select all`}
              selectedCount={props.item.values?.length || 0}
              formatSelectedCount={(count: string | number) => (
                <Plural value={count} one={'1 selected'} other={'# selected'} />
              )}
            />
          </Box>
        </Box>
      </PopoverContent>
    </Popover>
  )
}
